import * as z from 'zod';

/**
 * `localStorage` is written by prior app versions, other tabs, and
 * (occasionally) hand-edited by a user/support agent -- it's an external
 * boundary, not a value this build controls the shape of end to end.
 * Parses the raw string against `schema`, returning `fallback` (and
 * logging a warning, never throwing) on invalid JSON or a shape mismatch,
 * instead of letting `JSON.parse`/an unchecked `as T` cast crash the
 * caller or silently propagate a wrong-shaped value.
 */
export function readJsonFromStorage<S extends z.ZodType>(
	key: string,
	schema: S,
	fallback: z.infer<S>,
): z.infer<S> {
	const raw = localStorage.getItem(key);
	if (raw === null) {
		return fallback;
	}

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch (err) {
		console.warn(`[storageSchema] "${key}" is not valid JSON`, err);
		return fallback;
	}

	const result = schema.safeParse(parsed);
	if (!result.success) {
		console.warn(
			`[storageSchema] "${key}" failed schema validation`,
			result.error,
		);
		return fallback;
	}

	return result.data;
}
